import { Shield, Heart, Users, ArrowRight, Sparkles, BadgeCheck, MessageCircle, Lock } from 'lucide-react'
import Header from './Header'
import Footer from './Footer'
import { useOffline } from '../hooks/useOffline'
import { FAQ_ITEMS } from '../data/faq'
import FAQAccordion from './FAQAccordion'
import { useScrollReveal } from '../hooks/useScrollReveal.ts'
import WaitlistForm from './WaitlistForm'

const APP_URL = 'https://app.safepsy.com'
const START_ARIA = 'Start talking to Sage'

export default function Landing() {
  useOffline()

  useScrollReveal()

  return (
    <div className="min-h-screen flex flex-col relative">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute -top-32 -left-32 w-[42rem] h-[42rem] rounded-full bg-primary-400/20 blur-3xl"
          style={{ animation: 'pulse 6s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
        />
        <div className="absolute top-[28%] -right-24 w-[38rem] h-[38rem] rounded-full bg-secondary-400/20 blur-3xl animate-pulse" />
        <div
          className="absolute bottom-[18%] left-[-8rem] w-[34rem] h-[34rem] rounded-full bg-primary-400/15 blur-3xl"
          style={{ animation: 'pulse 8s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
        />
        <div className="absolute -bottom-24 right-[10%] w-[32rem] h-[32rem] rounded-full bg-secondary-400/15 blur-3xl animate-pulse" />
      </div>

      <Header />
      <main id="main-content" className="flex-1 relative" role="main" aria-label="Landing" tabIndex={-1}>
        <div className="container-max section-padding py-8 lg:py-12 space-y-14 lg:space-y-20 relative">

          <section className="mb-6 scroll-reveal" data-reveal>
            <div className="max-w-5xl mx-auto bg-white/70 dark:bg-black/30 backdrop-blur-sm rounded-3xl border border-neutral-dark/20 dark:border-white/20 p-6 sm:p-8 lg:p-12 hover-soft-fade text-center">
              <p className="inline-flex items-center gap-2 text-sm sm:text-base text-heading mb-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-full px-4 py-2">
                <Sparkles className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                Beta is live!
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl text-heading leading-tight font-normal fade-in">
                <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent font-bold">
                  AI mental-health companion,
                  <br />
                  Built for your privacy
                </span>
              </h1>
              <div className="mt-6 max-w-3xl mx-auto space-y-4 text-base sm:text-lg text-body leading-relaxed whitespace-normal break-words [text-wrap:pretty]">
                <p>
                  Built by psychologists, for private reflection.
                </p>
                <p>
                  Store your encrypted history on your Decentralized Identity (DID), with ownership and access controlled exclusively by you.
                </p>
                <p>
                  Guest chats stay on your device and are not saved on any server or database. We do not use your chats to train our models.
                </p>
              </div>
            </div>
          </section>

          <section className="scroll-reveal" data-reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl text-heading mb-8 text-center">
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">How it works</span>
            </h2>
            <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-5">
              <div className="p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 hover-soft-fade">
                <MessageCircle className="w-7 h-7 text-primary-600 dark:text-primary-400 mb-3" />
                <p className="text-body text-sm sm:text-base"><strong className="text-heading">1. Start talking.</strong> No email, no password. Guest conversations stay on your device and are limited.</p>
              </div>
              <div className="p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 hover-soft-fade">
                <Heart className="w-7 h-7 text-secondary-600 dark:text-secondary-400 mb-3" />
                <p className="text-body text-sm sm:text-base"><strong className="text-heading">2. Get a reflective reply.</strong> Sage helps you to structure your thoughts, understand them and provides safe insights.</p>
              </div>
              <div className="p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 hover-soft-fade">
                <Lock className="w-7 h-7 text-accent-600 dark:text-accent-400 mb-3" />
                <p className="text-body text-sm sm:text-base"><strong className="text-heading">3. Keep a history only if you choose.</strong> Connect a wallet to save encrypted conversations. You hold the keys.</p>
              </div>
            </div>
          </section>

          <section id="privacy" className="scroll-reveal" data-reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl text-heading mb-8 text-center">
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">Why people use SafePsy</span>
            </h2>
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-5">
              <div className="p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 hover-soft-fade">
                <BadgeCheck className="w-7 h-7 text-secondary-600 dark:text-secondary-400 mb-3" />
                <p className="text-body text-sm sm:text-base"><strong className="text-heading">Start without an account:</strong> Guest chat is limited and is not saved on any server or database. You can try Sage before you decide to keep a history.</p>
              </div>
              <div className="p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 hover-soft-fade">
                <Shield className="w-7 h-7 text-primary-600 dark:text-primary-400 mb-3" />
                <p className="text-body text-sm sm:text-base"><strong className="text-heading">Saved chats are encrypted at rest:</strong> When you save a conversation, it is stored as ciphertext. Keys come from your wallet and are not kept on our servers.</p>
              </div>
              <div className="p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 hover-soft-fade">
                <Users className="w-7 h-7 text-accent-600 dark:text-accent-400 mb-3" />
                <p className="text-body text-sm sm:text-base"><strong className="text-heading">Clear limits:</strong> Sage is not a substitute for licensed therapy and does not provide diagnosis or medical treatment. If you are in crisis, contact emergency services or a licensed professional.</p>
              </div>
              <div className="p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 hover-soft-fade">
                <Heart className="w-7 h-7 text-secondary-600 dark:text-secondary-400 mb-3" />
                <p className="text-body text-sm sm:text-base"><strong className="text-heading">Private words, honest processing:</strong> Saved chats are stored as ciphertext off-chain; the DID holds identity and references, not the readable history. To reply, our service and a language-model compliant provider read the message over HTTPS.</p>
              </div>
            </div>
          </section>

          <section className="scroll-reveal" data-reveal>
            <WaitlistForm
              title="Get product updates"
              subtitle="The app is open to try. Leave your email if you want occasional notes on what we ship."
              submitLabel="Send me updates"
              submittingLabel="Sending..."
              formAriaLabel="Sign up for product updates"
              submitAriaLabel="Sign up for SafePsy product updates"
              helpText="Enter a valid email address to receive product updates"
              consentError="You must give consent to receive updates"
              consentLabel="I consent to SafePsy collecting and processing my personal data for the purpose of receiving product updates. I have read and agree to the Terms of Service and Security and Privacy Policy. *"
            />
          </section>

          <section id="faq" className="scroll-reveal" data-reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl text-heading mb-8 text-center">
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                Frequently asked questions
              </span>
            </h2>
            <FAQAccordion items={FAQ_ITEMS} answerClassName="text-xs sm:text-sm" />
          </section>
        </div>
      </main>
      <div className="fixed bottom-4 right-4 z-40">
        <a
          href={APP_URL}
          className="btn-try-beta"
          aria-label={START_ARIA}
        >
          Start talking
          <ArrowRight className="w-5 h-5 shrink-0" aria-hidden="true" />
        </a>
      </div>
      <Footer />

      {/* FAQPage Structured Data for AI Search */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_ITEMS.map((item) => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.a
            }
          }))
        })
      }} />
    </div>
  )
}
